import { createFileRoute } from '@tanstack/react-router'
import { Badge } from '@workspace/ui/components/badge'
import { Button } from '@workspace/ui/components/button'
import type { TableColumn } from '@workspace/ui/components/data-table'
import { CellPrimary, CellText, DataTable } from '@workspace/ui/components/data-table'
import { Input } from '@workspace/ui/components/input'
import { StatCell } from '@workspace/ui/components/stat-cell'
import { useState } from 'react'
import {
  useAdminDecideInvestorCheckMutation,
  useAdminInvestorChecksQuery,
} from '../../../features/admin/hooks/use-admin-api'

export const Route = createFileRoute('/admin/investor-checks/')({
  component: AdminInvestorChecksPage,
})

const PAGE_SIZE = 25
const DEFAULT_METHOD = 'Public profile and fund website'
const STATUSES = ['PENDING', 'PASSED', 'FAILED'] as const
type Status = (typeof STATUSES)[number]

type Item = NonNullable<
  ReturnType<typeof useAdminInvestorChecksQuery>['data']
>['data']['items'][number]

function formatDate(value: Date | string) {
  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function AdminInvestorChecksPage() {
  const [status, setStatus] = useState<Status>('PENDING')
  const [page, setPage] = useState(1)
  const [methods, setMethods] = useState<Record<string, string>>({})
  const query = useAdminInvestorChecksQuery({ status, page, limit: PAGE_SIZE })
  const decide = useAdminDecideInvestorCheckMutation()
  const data = query.data?.data
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1

  const methodFor = (id: string) => methods[id] ?? DEFAULT_METHOD
  const submit = (item: Item, decision: 'PASSED' | 'FAILED') =>
    decide.mutate({
      path: { membershipId: item.membershipId },
      body: { status: decision, method: methodFor(item.membershipId).trim() || DEFAULT_METHOD },
    })

  const columns: TableColumn<Item>[] = [
    {
      header: 'Person',
      render: (item) => (
        <div className="space-y-0.5">
          <CellPrimary>{item.user.name ?? item.user.email}</CellPrimary>
          <CellText className="block text-xs">{item.user.email}</CellText>
          {item.position && <CellText className="block text-xs">{item.position}</CellText>}
        </div>
      ),
    },
    {
      header: 'Firm',
      render: (item) => (
        <div className="space-y-0.5">
          <CellPrimary>{item.firm.name}</CellPrimary>
          <CellText className="block text-xs">
            {item.firm.websiteUrl ?? 'No website'} ·{' '}
            {item.firm.investorType?.replace('_', ' ').toLowerCase() ?? 'type unknown'}
          </CellText>
          <CellText className="block text-xs">
            {item.firm.passedMemberCount} of {item.firm.memberCount} checked
            {item.firm.foundingNumber ? ` · founding place ${item.firm.foundingNumber}` : ''}
          </CellText>
        </div>
      ),
    },
    {
      header: 'Email',
      width: '170px',
      render: (item) =>
        item.emailMatchesFirmWebsite ? (
          <Badge variant="secondary">On the firm domain</Badge>
        ) : item.user.isGenericEmail ? (
          <Badge variant="outline">Generic mailbox</Badge>
        ) : (
          <Badge variant="destructive">Different domain</Badge>
        ),
    },
    {
      header: 'Requested',
      width: '120px',
      render: (item) => <CellText>{formatDate(item.requestedAt)}</CellText>,
    },
    {
      header: status === 'PENDING' ? 'Decision' : 'Decided',
      width: '320px',
      render: (item) =>
        item.checkStatus === 'PENDING' ? (
          <div className="space-y-2">
            <Input
              aria-label="How the check was done"
              value={methodFor(item.membershipId)}
              onChange={(e) => setMethods({ ...methods, [item.membershipId]: e.target.value })}
            />
            <div className="flex gap-2">
              <Button size="sm" disabled={decide.isPending} onClick={() => submit(item, 'PASSED')}>
                Pass
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={decide.isPending}
                onClick={() => submit(item, 'FAILED')}
              >
                Fail
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-0.5">
            <CellText className="block">
              {item.checkStatus === 'PASSED' ? 'Passed' : 'Failed'}
              {item.checkedAt ? ` on ${formatDate(item.checkedAt)}` : ''}
            </CellText>
            <CellText className="block text-xs">{item.checkMethod ?? ''}</CellText>
            <div className="flex gap-2 pt-1">
              {item.checkStatus === 'FAILED' && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={decide.isPending}
                  onClick={() => submit(item, 'PASSED')}
                >
                  Pass instead
                </Button>
              )}
              {item.checkStatus === 'PASSED' && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={decide.isPending}
                  onClick={() => submit(item, 'FAILED')}
                >
                  Fail instead
                </Button>
              )}
            </div>
          </div>
        ),
    },
  ]

  return (
    <div className="mx-auto max-w-6xl space-y-6 py-2">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">Investor checks</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          The check SeedForge promises investors: public information, email and the fund's domain.
          Until a person passes, their AI gets search results only and their follows wait. The first
          person of each of the first 100 investment firms to pass also gives their firm a founding
          place.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        <StatCell
          size="sm"
          value={data?.pendingTotal ?? '-'}
          label="Waiting for a check"
          accent={(data?.pendingTotal ?? 0) > 0}
        />
        <StatCell size="sm" value={data?.foundingPlacesLeft ?? '-'} label="Founding places left" />
        <StatCell
          size="sm"
          value={data?.total ?? '-'}
          label={`${status.toLowerCase()} in this list`}
        />
      </div>

      <div className="flex gap-2">
        {STATUSES.map((value) => (
          <Button
            key={value}
            size="sm"
            variant={status === value ? 'default' : 'outline'}
            onClick={() => {
              setStatus(value)
              setPage(1)
            }}
          >
            {value.charAt(0) + value.slice(1).toLowerCase()}
          </Button>
        ))}
      </div>

      <DataTable
        columns={columns}
        data={data?.items ?? []}
        isLoading={query.isLoading}
        emptyMessage={status === 'PENDING' ? 'Nobody is waiting for a check.' : 'Nothing here.'}
        keyExtractor={(item) => item.membershipId}
        minWidth="1000px"
      />

      <div className="flex items-center justify-end gap-2 text-sm text-muted-foreground">
        <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>
          Previous
        </Button>
        <span>
          Page {page} of {totalPages}
        </span>
        <Button
          size="sm"
          variant="outline"
          disabled={page >= totalPages}
          onClick={() => setPage(page + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  )
}
